"use client";

import { Children, useEffect, useMemo, useState, type ReactNode } from "react";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@monogo/design-system";

const DESKTOP_SLIDES_CLASS: Record<number, string> = {
  2: "lg:basis-1/2",
  3: "lg:basis-1/3",
  4: "lg:basis-1/4",
  5: "lg:basis-1/5",
  6: "lg:basis-1/6",
};

/**
 * Carousel of server-rendered product tiles (2 / 3 / `desktopSlides` per view).
 * It moves a view at a time; `dots` adds one dot per view (`data-carousel-dots`,
 * styled by the host), `arrows` the previous / next buttons from 768px.
 */
export function MagentoProductTileCarousel({
  children,
  desktopSlides = 4,
  autoplay = false,
  autoplayDelay = 5000,
  loop = true,
  arrows = true,
  dots = false,
}: {
  children: ReactNode;
  desktopSlides?: number;
  autoplay?: boolean;
  autoplayDelay?: number;
  loop?: boolean;
  arrows?: boolean;
  dots?: boolean;
}) {
  const plugins = useMemo(
    () => (autoplay ? [Autoplay({ delay: autoplayDelay, stopOnInteraction: true, stopOnMouseEnter: true })] : []),
    [autoplay, autoplayDelay]
  );
  const [api, setApi] = useState<CarouselApi>();
  const [snaps, setSnaps] = useState<number[]>([]);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api || !dots) return;
    const sync = () => {
      setSnaps(api.scrollSnapList());
      setSelected(api.selectedScrollSnap());
    };
    sync();
    api.on("select", sync).on("reInit", sync);
    return () => {
      api.off("select", sync).off("reInit", sync);
    };
  }, [api, dots]);

  return (
    <Carousel
      opts={{ align: "start", loop, slidesToScroll: "auto" }}
      plugins={plugins}
      setApi={setApi}
      className="relative w-full"
    >
      <CarouselContent className="-ml-4">
        {Children.toArray(children).map((child, index) => (
          <CarouselItem
            key={index}
            className={`basis-1/2 pl-4 md:basis-1/3 ${DESKTOP_SLIDES_CLASS[desktopSlides] ?? DESKTOP_SLIDES_CLASS[4]}`}
          >
            {child}
          </CarouselItem>
        ))}
      </CarouselContent>
      {arrows ? (
        <>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </>
      ) : null}
      {dots && snaps.length > 1 ? (
        <ul data-carousel-dots="">
          {snaps.map((_, index) => (
            <li key={index} className={index === selected ? "is-active" : undefined}>
              <button
                type="button"
                aria-label={`${index + 1} / ${snaps.length}`}
                aria-current={index === selected ? "true" : undefined}
                onClick={() => api?.scrollTo(index)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </Carousel>
  );
}
